/**
 * NIS2 Article 23 — the reporting clocks.
 *
 * WHY THIS IS IN THE SHARED PACKAGE
 * A countdown that the server and the screen disagree about is worse than no
 * countdown. The API computes deadlines for the exports and the record; the
 * browser recomputes them every second so the clock ticks without polling. Both
 * import this file, so there is exactly one implementation of "when is this
 * due" in the product.
 *
 * ---------------------------------------------------------------------------
 *  WHAT THE DIRECTIVE ACTUALLY SAYS, AND WHAT IT DOES NOT
 * ---------------------------------------------------------------------------
 * Article 23(4) of Directive (EU) 2022/2555 sets a staged sequence. Paraphrased
 * from the text, with the parts that matter to the arithmetic in full:
 *
 *   (a) EARLY WARNING — "without undue delay and in any event within 24 hours
 *       of having become aware of the significant incident".
 *   (b) INCIDENT NOTIFICATION — "without undue delay and in any event within 72
 *       hours of having become aware of the significant incident".
 *   (c) INTERMEDIATE REPORT — "upon the request of a CSIRT or, where
 *       applicable, the competent authority". NO DEADLINE IS SET: it exists
 *       only when asked for, so this module never invents one.
 *   (d) FINAL REPORT — "not later than one month after the submission of the
 *       incident notification under point (b)".
 *   (e) PROGRESS REPORT — where the incident is still ongoing when the final
 *       report is due, a progress report at that time and a final report
 *       within one month of the handling of the incident being completed.
 *
 * THE DETAIL THAT IS EASIEST TO GET WRONG, AND WE DO NOT:
 * the one-month clock in (d) runs from the SUBMISSION OF THE 72-HOUR
 * NOTIFICATION — not from awareness, and not from the 72-hour deadline. Until
 * that notification is actually recorded as submitted, the final-report
 * deadline is genuinely unknown, and this module returns `NOT_YET_SCHEDULED`
 * rather than guessing. A guessed deadline that is a day early is a customer
 * doing unnecessary work at 3am; one that is a day late is a breach.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT DECIDE
 *   * Whether an incident is "significant". Article 23(3) gives qualitative
 *     criteria; the quantitative thresholds come from national transposition
 *     and, for some entity types only, from Commission Implementing Regulation
 *     (EU) 2024/2690. There is no single EU-wide number, so we do not assert
 *     one and the interface says the judgement is the customer's.
 *   * Who the report goes to. The CSIRT or competent authority, the channel and
 *     the form are national. The customer records who they sent it to; we never
 *     pre-fill an authority we cannot know.
 *   * Sectoral derogations (for example the shorter deadline the Directive sets
 *     for trust service providers in respect of their trust services) and any
 *     national rule that is stricter than the Directive's floor. Member States
 *     may transpose more demanding requirements, so these are presented as
 *     the Directive's outer limits, which is what they are.
 *
 * "WITHOUT UNDUE DELAY" IS THE ACTUAL OBLIGATION. 24 and 72 hours are the outer
 * limits, not a budget to spend. The interface says so; this file only does the
 * arithmetic of the outer limits, because that is the only part that is a
 * number.
 */
/** The reporting stages of Article 23(4), in the order they occur. */
export const INCIDENT_STAGES = [
    'EARLY_WARNING',
    'NOTIFICATION',
    'INTERMEDIATE',
    'FINAL',
    'PROGRESS',
];
/** The stages that carry a deadline derived from a date we hold. */
export const SCHEDULED_STAGES = ['EARLY_WARNING', 'NOTIFICATION', 'FINAL'];
/** Article 23(4)(a) and (b), in hours from awareness. */
export const EARLY_WARNING_HOURS = 24;
export const NOTIFICATION_HOURS = 72;
/** A report is a draft until someone records that they sent it. */
export const INCIDENT_REPORT_STATUSES = ['DRAFT', 'SUBMITTED'];
/**
 * How the customer sent it. Recorded because "we notified on the 2nd" is only
 * evidence if it says by what route — an authority's portal issues a reference,
 * an e-mail has a sent copy, a phone call has neither.
 */
export const INCIDENT_SUBMISSION_CHANNELS = ['PORTAL', 'EMAIL', 'PHONE', 'POST', 'OTHER'];
/**
 * Severity as the organisation judges it.
 *
 * This is the ENTITY'S OWN assessment, and it is not the same thing as whether
 * the incident is "significant" under Article 23(3) — that is a separate
 * judgement with nationally-set thresholds, held in its own field. Collapsing
 * the two would be the product quietly deciding a notification obligation.
 */
export const INCIDENT_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
/** Lifecycle of the incident itself, not of its reports. */
export const INCIDENT_STATUSES = ['OPEN', 'CONTAINED', 'RESOLVED', 'CLOSED'];
/**
 * A tri-state answer. `UNKNOWN` is a first-class value and the default, because
 * at hour two the honest answer to "was this malicious?" is usually that nobody
 * knows yet — and the early warning is explicitly allowed to say so.
 */
export const INCIDENT_TRISTATE = ['YES', 'NO', 'UNKNOWN'];
/**
 * Add one calendar month, clamping to the end of the target month.
 *
 * "One month" is not thirty days. Under Regulation (EEC) No 1182/71 — the EU's
 * own rule for reckoning periods — a period in months ends on the same date in
 * the later month, and where that date does not exist it ends on the last day
 * of that month. So 31 January + 1 month is 28 (or 29) February, not 2 March,
 * which is what naive date arithmetic produces.
 *
 * Precise reckoning can still differ by national administrative practice; the
 * interface says the final-report date is our calculation and that the
 * authority's own reckoning governs.
 */
export function addCalendarMonth(from) {
    const year = from.getUTCFullYear();
    const month = from.getUTCMonth();
    const day = from.getUTCDate();
    // Day 0 of the following month is the last day of the target month.
    const lastDayOfTargetMonth = new Date(Date.UTC(year, month + 2, 0)).getUTCDate();
    return new Date(Date.UTC(year, month + 1, Math.min(day, lastDayOfTargetMonth), from.getUTCHours(), from.getUTCMinutes(), from.getUTCSeconds(), from.getUTCMilliseconds()));
}
/** Inside the last quarter of a window, a deadline is called "due soon". */
const DUE_SOON_FRACTION = 0.25;
function describe(stage, dueAt, basis, windowMs, submittedAt, now) {
    if (!dueAt) {
        return {
            stage,
            dueAt: null,
            basis,
            state: 'NOT_YET_SCHEDULED',
            msRemaining: null,
            msLateBy: null,
            submittedAt: submittedAt ? submittedAt.toISOString() : null,
        };
    }
    const msRemaining = dueAt.getTime() - now.getTime();
    if (submittedAt) {
        const lateBy = submittedAt.getTime() - dueAt.getTime();
        return {
            stage,
            dueAt: dueAt.toISOString(),
            basis,
            state: lateBy > 0 ? 'SUBMITTED_LATE' : 'SUBMITTED_ON_TIME',
            msRemaining,
            msLateBy: lateBy > 0 ? lateBy : null,
            submittedAt: submittedAt.toISOString(),
        };
    }
    let state = 'OPEN';
    if (msRemaining <= 0)
        state = 'OVERDUE';
    else if (windowMs !== null && msRemaining <= windowMs * DUE_SOON_FRACTION)
        state = 'DUE_SOON';
    return {
        stage,
        dueAt: dueAt.toISOString(),
        basis,
        state,
        msRemaining,
        msLateBy: null,
        submittedAt: null,
    };
}
function toDate(value) {
    if (!value)
        return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
/**
 * The three computable deadlines, in order.
 *
 * The intermediate report (c) and the progress report (e) are not here: (c) has
 * no deadline until an authority asks for one, and (e) depends on when handling
 * of the incident is completed, which is a fact about the future. Both are
 * offered in the interface as stages the customer can prepare; neither gets an
 * invented clock.
 */
export function incidentDeadlines(input) {
    const now = input.now ?? new Date();
    const awareAt = toDate(input.awareAt);
    if (!awareAt)
        return [];
    const earlyWindow = EARLY_WARNING_HOURS * 3_600_000;
    const notificationWindow = NOTIFICATION_HOURS * 3_600_000;
    const notificationSubmittedAt = toDate(input.notificationSubmittedAt);
    // (d) runs from the submission of (b). No submission, no deadline — see the
    // header note; this is the single most consequential line in the file.
    const finalDueAt = notificationSubmittedAt ? addCalendarMonth(notificationSubmittedAt) : null;
    return [
        describe('EARLY_WARNING', new Date(awareAt.getTime() + earlyWindow), 'AWARENESS', earlyWindow, toDate(input.earlyWarningSubmittedAt), now),
        describe('NOTIFICATION', new Date(awareAt.getTime() + notificationWindow), 'AWARENESS', notificationWindow, notificationSubmittedAt, now),
        describe('FINAL', finalDueAt, 'NOTIFICATION_SUBMITTED', 
        // A calendar month has no fixed length, so "due soon" for the final report
        // is measured against the actual window rather than a constant.
        finalDueAt && notificationSubmittedAt
            ? finalDueAt.getTime() - notificationSubmittedAt.getTime()
            : null, toDate(input.finalSubmittedAt), now),
    ];
}
/**
 * The stage the customer should act on next, or null when nothing is pending.
 *
 * An overdue stage outranks an open one: a missed early warning still has to be
 * sent, and sending it is more urgent than starting the notification. This is
 * what drives the single call to action on the incident screen — at 2am a
 * person should not have to work out which of five panels to open.
 */
export function nextAction(deadlines) {
    const outstanding = deadlines.filter((d) => d.state === 'OVERDUE' || d.state === 'DUE_SOON' || d.state === 'OPEN');
    if (outstanding.length === 0)
        return null;
    const overdue = outstanding.filter((d) => d.state === 'OVERDUE');
    if (overdue.length > 0) {
        // The oldest breach first: it has been outstanding longest.
        return overdue.sort((a, b) => (a.msRemaining ?? 0) - (b.msRemaining ?? 0))[0];
    }
    return outstanding.sort((a, b) => (a.msRemaining ?? 0) - (b.msRemaining ?? 0))[0];
}
/** True when any deadline has passed unmet. Drives the register's red flag. */
export function hasMissedDeadline(deadlines) {
    return deadlines.some((d) => d.state === 'OVERDUE' || d.state === 'SUBMITTED_LATE');
}
/**
 * A duration as "2d 4h", "4h 12m" or "7m 30s", largest two units only.
 *
 * Seconds appear only under an hour, which is exactly when a person watching a
 * 24-hour clock starts to care about them. Above that they are noise that makes
 * the number flicker.
 */
export function formatDuration(ms) {
    const total = Math.max(0, Math.floor(Math.abs(ms) / 1000));
    const days = Math.floor(total / 86_400);
    const hours = Math.floor((total % 86_400) / 3_600);
    const minutes = Math.floor((total % 3_600) / 60);
    const seconds = total % 60;
    if (days > 0)
        return `${days}d ${hours}h`;
    if (hours > 0)
        return `${hours}h ${minutes}m`;
    if (minutes > 0)
        return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
}
//# sourceMappingURL=nis2Incident.js.map